import { DEFAULT_TAB_REDIRECT_PATH } from "@/utils/constants";
import { ProjectPanelRoute, projectPanelPath } from "@/types/routes";

/** How long a saved "last visited panel" stays eligible to resume into on the next project open. */
export const PROJECT_LAST_VISITED_PANEL_TTL_MS = 72 * 60 * 60 * 1000;

const STORAGE_KEY_PREFIX = "aiStudio.lastVisitedProjectPanel.";

interface LastVisitedPanelEntry {
  readonly path: string;
  readonly savedAt: number;
}

function storageKey(projectId: string): string {
  return `${STORAGE_KEY_PREFIX}${projectId}`;
}

function parseLastVisitedPanelEntry(raw: string | null): LastVisitedPanelEntry | null {
  if (raw === null) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<LastVisitedPanelEntry> | null;
    if (typeof parsed?.path !== "string" || parsed.path.length === 0 || typeof parsed.savedAt !== "number") {
      return null;
    }

    return { path: parsed.path, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

/** Whether a saved entry is still within {@link PROJECT_LAST_VISITED_PANEL_TTL_MS} of when it was saved. */
export function isLastVisitedPanelEntryFresh(entry: LastVisitedPanelEntry | null, now: number = Date.now()): entry is LastVisitedPanelEntry {
  return entry !== null && now - entry.savedAt < PROJECT_LAST_VISITED_PANEL_TTL_MS;
}

/** Remembers `path` as the panel to resume into next time `projectId` is opened (see `readLastVisitedProjectPanelPath`). */
export function saveLastVisitedProjectPanelPath(projectId: string, path: string, now: number = Date.now()): void {
  try {
    globalThis.localStorage.setItem(storageKey(projectId), JSON.stringify({ path, savedAt: now } satisfies LastVisitedPanelEntry));
  } catch {
    // Storage unavailable (private mode / quota) - resuming is a nice-to-have, not required.
  }
}

/** Last-visited panel path for `projectId`, or `null` on the first-ever visit or once the 72h window has elapsed. */
export function readLastVisitedProjectPanelPath(projectId: string, now: number = Date.now()): string | null {
  try {
    const entry = parseLastVisitedPanelEntry(globalThis.localStorage.getItem(storageKey(projectId)));
    return isLastVisitedPanelEntryFresh(entry, now) ? entry.path : null;
  } catch {
    return null;
  }
}

/** Where opening `projectId` should land: the resumed last-visited panel, or the default tab (first visit / expired). */
export function resolveProjectEntryPath(projectId: string, now: number = Date.now()): string {
  return readLastVisitedProjectPanelPath(projectId, now) ?? projectPanelPath(projectId, DEFAULT_TAB_REDIRECT_PATH);
}

const LAST_VISITED_PROJECT_STORAGE_KEY = "aiStudio.lastVisitedProject";

interface LastVisitedProjectEntry {
  readonly projectId: string;
  readonly savedAt: number;
}

function parseLastVisitedProjectEntry(raw: string | null): LastVisitedProjectEntry | null {
  if (raw === null) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<LastVisitedProjectEntry> | null;
    if (typeof parsed?.projectId !== "string" || parsed.projectId.length === 0 || typeof parsed.savedAt !== "number") {
      return null;
    }

    return { projectId: parsed.projectId, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

export function saveLastVisitedProject(projectId: string, now: number = Date.now()): void {
  try {
    globalThis.localStorage.setItem(LAST_VISITED_PROJECT_STORAGE_KEY, JSON.stringify({ projectId, savedAt: now } satisfies LastVisitedProjectEntry));
  } catch {
    // ignore
  }
}

export function readLastVisitedProjectId(now: number = Date.now()): string | null {
  try {
    const entry = parseLastVisitedProjectEntry(globalThis.localStorage.getItem(LAST_VISITED_PROJECT_STORAGE_KEY));
    if (entry === null || now - entry.savedAt >= PROJECT_LAST_VISITED_PANEL_TTL_MS) {
      return null;
    }

    return readLastVisitedProjectPanelPath(entry.projectId, now) === null ? null : entry.projectId;
  } catch {
    return null;
  }
}

export function clearLastVisitedProject(): void {
  try {
    globalThis.localStorage.removeItem(LAST_VISITED_PROJECT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export type ProjectEntityGroup = "characters" | "locations" | "shots";

const ENTITY_STORAGE_KEY_PREFIX = "aiStudio.lastVisitedProjectEntity.";

interface LastVisitedEntityEntry {
  readonly entityId: string;
  readonly savedAt: number;
}

function entityStorageKey(projectId: string, group: ProjectEntityGroup): string {
  return `${ENTITY_STORAGE_KEY_PREFIX}${projectId}.${group}`;
}

export function projectPanelEntityGroup(panel: string | undefined): ProjectEntityGroup | null {
  switch (panel) {
    case ProjectPanelRoute.Characters:
      return "characters";
    case ProjectPanelRoute.Locations:
      return "locations";
    case ProjectPanelRoute.ImageFrame:
    case ProjectPanelRoute.Video:
      return "shots";
    default:
      return null;
  }
}

export function saveLastVisitedProjectEntityId(projectId: string, group: ProjectEntityGroup, entityId: string, now: number = Date.now()): void {
  try {
    globalThis.localStorage.setItem(entityStorageKey(projectId, group), JSON.stringify({ entityId, savedAt: now } satisfies LastVisitedEntityEntry));
  } catch {
    // ignore
  }
}

export function readLastVisitedProjectEntityId(projectId: string, group: ProjectEntityGroup, now: number = Date.now()): string | null {
  try {
    const raw = globalThis.localStorage.getItem(entityStorageKey(projectId, group));
    if (raw === null) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<LastVisitedEntityEntry> | null;
    if (typeof parsed?.entityId !== "string" || parsed.entityId.length === 0 || typeof parsed.savedAt !== "number") {
      return null;
    }

    return now - parsed.savedAt < PROJECT_LAST_VISITED_PANEL_TTL_MS ? parsed.entityId : null;
  } catch {
    return null;
  }
}
